import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { CloudUpload, Delete } from "@mui/icons-material";
import { Nominee } from "../types/nominee";
import { useAddNomineeMutation, useUpdateNomineeMutation } from "../api/nomineeApi";
import { useGetAwardAreasQuery } from '../../awardareas/api/awardareaApi';
import { AwardArea } from '../../awardareas/types/awardarea';

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  nominee?: Nominee | null;
  awardId: number;
}

const AddEditNominee: React.FC<Props> = ({ open, setModalOpen, onSuccess, nominee, awardId }) => {
  const [formData, setFormData] = useState<Nominee>({
    award_id: awardId,
    area_id: 0,
    name: "",
    bio_contribution: "",
    status: "suggested",
    nominator_name: "",
    nominator_email: "",
    nominator_phone: "",
    videos: [],
  });

  const [createNominee] = useAddNomineeMutation();
  const [updateNominee] = useUpdateNomineeMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [existingPhoto, setExistingPhoto] = useState<string | null>(null);

  const { data: awardAreasData } = useGetAwardAreasQuery();
  const awardAreas: AwardArea[] = awardAreasData?.data || [];

  useEffect(() => {
    if (nominee) {
      setFormData({
        ...nominee,
        videos: nominee.videos || [],
        award_id: nominee.award_id || awardId,
      });
      setExistingPhoto(nominee.photo || null);
      setPhotoFile(null);
    } else {
      setFormData({
        award_id: awardId,
        area_id: 0,
        name: "",
        bio_contribution: "",
        status: "suggested",
        nominator_name: "",
        nominator_email: "",
        nominator_phone: "",
        videos: [],
      });
      setExistingPhoto(null);
      setPhotoFile(null);
    }
  }, [nominee, open, awardId]);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setExistingPhoto(null);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setExistingPhoto(null);
  };

  const handleSubmit = async () => {
    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!formData.bio_contribution.trim()) {
      errors.bio_contribution = "Bio/Contribution is required.";
    }
    if (!formData.nominator_name.trim()) {
      errors.nominator_name = "Nominator name is required.";
    }
    if (!formData.nominator_email.trim()) {
      errors.nominator_email = "Nominator email is required.";
    }
    if (!formData.nominator_phone.trim()) {
      errors.nominator_phone = "Nominator phone is required.";
    }
    if (!formData.area_id) {
      errors.area_id = "Please select an award area.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmissionError(Object.values(errors)[0]);
      return;
    }
    setIsSubmitting(true);
    
    try {
      // Use FormData when there's a new photo or when editing with existing photo
      const hasNewPhoto = photoFile !== null;
      const isEditing = nominee?.id !== undefined;
      const useFormData = hasNewPhoto || (isEditing && existingPhoto !== null);
      
      if (useFormData) {
        const formDataToSend = new FormData();
        formDataToSend.append('award_id', formData.award_id.toString());
        formDataToSend.append('area_id', formData.area_id.toString());
        formDataToSend.append('name', formData.name);
        formDataToSend.append('bio_contribution', formData.bio_contribution);
        formDataToSend.append('status', formData.status);
        formDataToSend.append('nominator_name', formData.nominator_name);
        formDataToSend.append('nominator_email', formData.nominator_email);
        formDataToSend.append('nominator_phone', formData.nominator_phone);
        formDataToSend.append('videos', JSON.stringify(formData.videos ?? []));
        
        // Only send new photo file if there is one
        if (photoFile) {
          formDataToSend.append('photo', photoFile);
        }
        
        // Only send existing photo if editing, no new photo, and existing photo wasn't removed
        if (isEditing && !photoFile && existingPhoto) {
          formDataToSend.append('existing_photo', existingPhoto);
        }

        if (nominee?.id) {
          await updateNominee({ id: nominee.id, body: formDataToSend } as any).unwrap();
        } else {
          await createNominee({ body: formDataToSend } as any).unwrap();
        }
      } else {
        const body = {
          ...formData,
          videos: formData.videos ?? [],
        };
        
        if (nominee?.id) {
          await updateNominee({ id: nominee.id, body }).unwrap();
        } else {
          await createNominee({ body }).unwrap();
        }
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
      setFieldErrors({});
    } catch (err) {
      setSubmissionError("Failed to save nominee. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAreaChange = (e: any) => {
    setFormData({ ...formData, area_id: Number(e.target.value) });
  };

  const handleVideosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, videos: e.target.value.split(',').map(v => v.trim()).filter(Boolean) });
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{nominee ? "Edit Nominee" : "Add Nominee"}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel id="area-label">Award Area</InputLabel>
          <Select
            labelId="area-label"
            value={formData.area_id}
            label="Award Area"
            onChange={handleAreaChange}
            error={!!fieldErrors.area_id}
          >
            <MenuItem value={0} disabled>Select Area</MenuItem>
            {awardAreas.map((area) => (
              <MenuItem key={area.id} value={area.id}>{area.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          margin="dense"
          label="Nominee Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!fieldErrors.name}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Bio/Contribution"
          name="bio_contribution"
          value={formData.bio_contribution}
          onChange={handleChange}
          error={!!fieldErrors.bio_contribution}
          multiline
          minRows={3}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Nominator Name"
          name="nominator_name"
          value={formData.nominator_name}
          onChange={handleChange}
          error={!!fieldErrors.nominator_name}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Nominator Email"
          name="nominator_email"
          value={formData.nominator_email}
          onChange={handleChange}
          error={!!fieldErrors.nominator_email}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Nominator Phone"
          name="nominator_phone"
          value={formData.nominator_phone}
          onChange={handleChange}
          error={!!fieldErrors.nominator_phone}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Videos (comma separated URLs)"
          name="videos"
          value={formData.videos.join(', ')}
          onChange={handleVideosChange}
        />
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Photo
          </Typography>
          
          {/* Existing Photo */}
          {existingPhoto && !photoFile && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={existingPhoto}
                alt="Existing photo"
                style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
              />
              <IconButton
                onClick={handleRemovePhoto}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.dark' },
                  width: 24,
                  height: 24
                }}
                size="small"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          {/* New Photo Preview */}
          {photoFile && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={URL.createObjectURL(photoFile)}
                alt="Preview"
                style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
              />
              <IconButton
                onClick={handleRemovePhoto}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.dark' },
                  width: 24,
                  height: 24
                }}
                size="small"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
            sx={{ mt: 1 }}
          >
            Upload Photo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handlePhotoChange}
            />
          </Button>
        </Box>
        
        <FormControl fullWidth margin="dense">
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            value={formData.status}
            label="Status"
            name="status"
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          >
            <MenuItem value="suggested">Suggested</MenuItem>
            <MenuItem value="shortlisted">Shortlisted</MenuItem>
            <MenuItem value="winner">Winner</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        {submissionError && <Alert severity="error" sx={{ mt: 2 }}>{submissionError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} /> : (nominee ? "Update" : "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditNominee; 